import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PromptCard, describePermission } from "../components/PromptCard.tsx";
import { DiffFiles, repoLabel, SessionDiff, sessionRepos, useDiff } from "../components/SessionDiff.tsx";
import { Timeline, ToolRow } from "../components/Timeline.tsx";
import { Confirm, ErrorBox, Loading, Markdown, StatePill, Tabs, useAction, useNow, useToast } from "../components/ui.tsx";
import { api, del, errorText, post, refresh, useApi } from "../lib/api.ts";
import { clock, duration, fullTime, MODEL_SUGGESTIONS, relPath, shortId } from "../lib/format.ts";
import { useLive } from "../lib/live.ts";
import { buildTranscript, turnStartedAt, type TranscriptItem } from "../lib/transcript.ts";
import type { EventRow, Project, Prompt, Session, Settings } from "../lib/types.ts";

type Tab = "transcript" | "timeline" | "diff";

/** The full event log for a session: loaded once, then extended from the live socket (and back-filled after reconnects). */
function useSessionEvents(sessionId: string) {
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const last = useRef(0);

  const fetchAfter = useCallback(
    async (after: number) => {
      const all: EventRow[] = [];
      for (;;) {
        const page = await api<EventRow[]>(`/api/sessions/${sessionId}/events?after=${after}&limit=5000`);
        all.push(...page);
        if (page.length < 5000) break;
        after = page.at(-1)!.seq;
      }
      return all;
    },
    [sessionId],
  );

  const merge = useCallback((rows: EventRow[]) => {
    const fresh = rows.filter((r) => r.seq > last.current);
    if (!fresh.length) return;
    last.current = fresh.at(-1)!.seq;
    setEvents((cur) => [...(cur ?? []), ...fresh]);
  }, []);

  useEffect(() => {
    last.current = 0;
    setEvents(null);
    setError(null);
    fetchAfter(0)
      .then((rows) => {
        last.current = rows.at(-1)?.seq ?? 0;
        setEvents(rows);
      })
      .catch(setError);
  }, [fetchAfter]);

  useLive((msg) => {
    if (msg.kind === "hello") {
      // Reconnected: fill whatever we missed.
      if (events) void fetchAfter(last.current).then(merge).catch(() => {});
      return;
    }
    if (msg.kind !== "event" || msg.event.sessionId !== sessionId) return;
    const e = msg.event;
    if (e.seq === last.current + 1) merge([{ ...e, id: e.id ?? 0, createdAt: e.createdAt }]);
    else if (e.seq > last.current) void fetchAfter(last.current).then(merge).catch(() => {});
  });

  return { events, error };
}

export function SessionPage() {
  const { id = "" } = useParams();
  const session = useApi<Session>(`/api/sessions/${id}`);
  const s = session.data;
  const project = useApi<Project>(s ? `/api/projects/${s.projectId}` : null);
  const prompts = useApi<Prompt[]>(`/api/sessions/${id}/prompts`);
  const settings = useApi<Settings>("/api/settings");
  const { events, error } = useSessionEvents(id);
  const [tab, setTab] = useState<Tab>("transcript");
  const now = useNow(1000);

  const built = useMemo(() => (events ? buildTranscript(events) : null), [events]);
  const pending = new Set((prompts.data ?? []).filter((p) => !p.resolvedAt).map((p) => p.id));
  const lastResult = [...(built?.items ?? [])].reverse().find((i) => i.kind === "result");
  const lastSummary = lastResult?.kind === "result" ? lastResult.payload.text : undefined;

  if (session.error) return <ErrorBox error={session.error} />;
  if (!s) return <Loading />;
  const p = project.data;
  const guild = settings.data?.["discord.guild_id"];
  const thread = typeof guild === "string" && guild && s.threadId ? `https://discord.com/channels/${guild}/${s.threadId}` : null;
  const started = events ? turnStartedAt(events) : null;
  const inTurn = ["working", "waiting", "rate_limited"].includes(s.state);

  return (
    <>
      <div className="pagehead">
        <div className="stack tight">
          <div className="muted small">
            <Link to="/sessions">Sessions</Link> / {p ? <Link to={`/projects/${p.slug}`}>{p.slug}</Link> : "…"}
          </div>
          <h1>{s.name}</h1>
          <div className="row small">
            <StatePill state={s.state} reason={s.stateReason} />
            {s.kind === "remote" && <span className="pill" title="Interactive Claude Code with Remote Control">Remote Control</span>}
            {s.kind === "remote" && s.remoteUrl && s.live?.connected && (
              <a className="btn sm primary" href={s.remoteUrl} target="_blank" rel="noreferrer">
                Open in Claude app ↗
              </a>
            )}
            <span className="mono">{s.model ?? p?.defaultModel ?? "default model"}</span>
            {(s.effort ?? p?.defaultEffort) && <span className="mono muted">effort {s.effort ?? p?.defaultEffort}</span>}
            <span className="mono muted">{s.branch}</span>
            {inTurn && started && <span className="muted">{duration(now - Date.parse(started))}</span>}
            {s.live && s.live.queued > 0 && <span className="pill">{s.live.queued} queued</span>}
            {s.stateReason && !inTurn && <span className="muted">· {s.stateReason}</span>}
          </div>
        </div>
        <SessionActions session={s} thread={thread} inCheckout={p?.rootPath === s.worktreePath} />
      </div>

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "transcript", label: "Transcript" },
          { id: "timeline", label: `Timeline${built ? ` (${built.tools.length} tool calls)` : ""}` },
          { id: "diff", label: "Diff" },
        ]}
      />

      {tab === "diff" ? (
        <SessionDiff session={s} project={p} lastSummary={lastSummary} />
      ) : (
        <div className="split">
          <div className="stack" style={{ minWidth: 0 }}>
            {error ? <ErrorBox error={error} /> : !built ? <Loading /> : tab === "timeline" ? <Timeline tools={built.tools} /> : <Transcript items={built.items} pending={pending} />}
            {tab === "transcript" && <Composer session={s} />}
            <div id="transcript-end" />
          </div>
          <SessionSidebar session={s} project={p} onDiff={() => setTab("diff")} />
        </div>
      )}
    </>
  );
}

function SessionActions({ session: s, thread, inCheckout }: { session: Session; thread: string | null; inCheckout: boolean }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState<null | "kill" | "delete">(null);
  const [deleteBranch, setDeleteBranch] = useState(false);
  const act = (path: string, msg: string) => async () => {
    await post(`/api/sessions/${s.id}/${path}`);
    refresh(`/api/sessions/${s.id}`, "/api/overview");
    return msg;
  };
  const [interrupt, interrupting] = useAction(act("interrupt", "Interrupted"), (m) => m);
  const [stop, stopping] = useAction(act("stop", "Stopping after the current turn"), (m) => m);
  const [resume, resuming] = useAction(act("resume", "Resuming"), (m) => m);
  const live = ["idle", "working", "waiting", "done", "rate_limited"].includes(s.state);

  return (
    <div className="row">
      {thread && (
        <a className="btn" href={thread} target="_blank" rel="noreferrer">
          Open thread ↗
        </a>
      )}
      {["working", "waiting", "rate_limited"].includes(s.state) && (
        <button className="btn" disabled={interrupting} onClick={() => interrupt()}>
          Interrupt
        </button>
      )}
      {(live || s.state === "queued") && (
        <button className="btn" disabled={stopping} onClick={() => stop()}>
          Stop
        </button>
      )}
      {["stopped", "errored", "queued"].includes(s.state) && (
        <button className="btn primary" disabled={resuming} onClick={() => resume()}>
          Resume
        </button>
      )}
      {live && (
        <button className="btn" onClick={() => setConfirm("kill")} title="Stop without waiting for the turn to finish">
          Kill
        </button>
      )}
      <button className="btn danger" onClick={() => setConfirm("delete")}>
        Delete
      </button>
      {confirm === "kill" && (
        <Confirm title="Kill this session?" confirmLabel="Kill" danger onClose={() => setConfirm(null)} onConfirm={act("kill", "")}>
          <p>The runner is terminated without finishing the current turn. The Claude session is kept, so Resume continues the conversation.</p>
        </Confirm>
      )}
      {confirm === "delete" && (
        <Confirm
          title={`Delete ${s.name}?`}
          confirmLabel="Delete session"
          danger
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await del(`/api/sessions/${s.id}`, { deleteBranch: deleteBranch && !inCheckout });
            refresh("/api/sessions", "/api/overview", "/api/projects/");
            navigate("/sessions");
          }}
        >
          {inCheckout ? (
            <p>Stops the runner. It worked in the project checkout, so its files and branch stay. The transcript stays in the database.</p>
          ) : (
            <>
              <p>Stops the runner and removes the worktree. The transcript stays in the database.</p>
              <label className="check">
                <input type="checkbox" checked={deleteBranch} onChange={(e) => setDeleteBranch(e.target.checked)} />
                <span>
                  Also delete the branch <code>{s.branch}</code>
                </span>
              </label>
            </>
          )}
        </Confirm>
      )}
    </div>
  );
}

function Who({ name, at, extra }: { name: string; at: string; extra?: string }) {
  return (
    <div className="who">
      <b>{name}</b>
      <time title={fullTime(at)}>{clock(at)}</time>
      {extra && <span>· {extra}</span>}
    </div>
  );
}

function resolutionText(r: { via: string; by: string; summary: string }, kind: "question" | "permission") {
  if (r.summary === "cancelled") return "✖ Cancelled by an interrupt";
  const who = r.by.startsWith("discord:") ? "Discord" : r.via === "web" ? "the dashboard" : r.by;
  if (kind === "permission") return `✓ ${({ allow: "Approved", deny: "Denied", always: "Always allowed" } as Record<string, string>)[r.summary.split(":")[0]!] ?? r.summary} via ${who}`;
  return `✓ Answered via ${who}: ${r.summary}`;
}

function Transcript({ items, pending }: { items: TranscriptItem[]; pending: Set<string> }) {
  const first = useRef(true);
  // Whether you were at the bottom before new output arrived (measured on scroll, not after the page grew).
  const following = useRef(true);
  useEffect(() => {
    const onScroll = () => {
      const marker = document.getElementById("transcript-end");
      following.current = marker ? marker.getBoundingClientRect().bottom <= window.innerHeight + 160 : true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Follow new output when you're at the bottom; jump there on first load.
  useLayoutEffect(() => {
    // Bring the marker under the composer into view: the last message then sits just above the composer,
    // on desktop (sticky composer) and on phones (where the sidebar cards follow below).
    if (first.current || following.current) document.getElementById("transcript-end")?.scrollIntoView({ block: "end" });
    first.current = false;
  }, [items.length, pending.size]);

  if (!items.length) return <div className="empty">No messages yet. Send the first prompt below.</div>;
  return (
    <div className="transcript">
      {items.map((it) => {
        switch (it.kind) {
          case "user":
            return (
              <div key={it.seq} className="msg">
                <Who name={it.via === "discord" ? "You (Discord)" : it.via === "web" ? "You" : it.via === "app" ? "You (Claude app)" : "System"} at={it.at} />
                <div className="bubble user">
                  <Markdown text={it.text} />
                </div>
              </div>
            );
          case "assistant":
            return (
              <div key={it.seq} className="msg">
                <Who name="Claude" at={it.at} />
                <div className="bubble">
                  <Markdown text={it.text} />
                </div>
              </div>
            );
          case "tools":
            return (
              <div key={it.seq} className="msg">
                <Who name="Tools" at={it.at} extra={`${it.calls.length} call${it.calls.length === 1 ? "" : "s"}${it.end !== it.at ? ` · ${duration(Date.parse(it.end) - Date.parse(it.at))}` : ""}`} />
                <div className="bubble tools stack tight">
                  {it.calls.map((c) => (
                    <ToolRow key={c.id} call={c} />
                  ))}
                </div>
              </div>
            );
          case "question":
            return (
              <div key={it.seq} className="msg">
                <Who name="Claude" at={it.at} extra="question" />
                {pending.has(it.promptId) ? (
                  <div className="prompt">
                    <PromptCard prompt={{ id: it.promptId, kind: "question", toolName: "AskUserQuestion", input: {}, options: it.questions }} />
                  </div>
                ) : (
                  <div className="bubble">
                    {it.questions.map((q, i) => (
                      <div key={i}>
                        <b>{q.question}</b>
                      </div>
                    ))}
                    {it.resolution && <div className="muted small" style={{ marginTop: 4 }}>{resolutionText(it.resolution, "question")}</div>}
                  </div>
                )}
              </div>
            );
          case "permission": {
            const d = describePermission(it.payload.toolName, it.payload.input);
            return (
              <div key={it.seq} className="msg">
                <Who name="Claude" at={it.at} extra="permission" />
                {pending.has(it.promptId) ? (
                  <div className="prompt permission">
                    <PromptCard prompt={{ id: it.promptId, kind: "permission", toolName: it.payload.toolName, input: it.payload.input, options: null }} />
                    {it.payload.reason && <div className="muted small">Reason: {it.payload.reason}</div>}
                  </div>
                ) : (
                  <div className="bubble">
                    <div>
                      {d.action}: <code>{d.body.split("\n")[0]}</code>
                    </div>
                    {it.resolution && <div className="muted small" style={{ marginTop: 4 }}>{resolutionText(it.resolution, "permission")}</div>}
                  </div>
                )}
              </div>
            );
          }
          case "result":
            return (
              <div key={it.seq} className="msg">
                <Who name={it.payload.isError ? "Turn ended with an error" : "Done"} at={it.at} extra={`${duration(it.payload.durationMs)}${it.payload.numTurns ? ` · ${it.payload.numTurns} turns` : ""}${it.payload.costUsd ? ` · $${it.payload.costUsd.toFixed(2)} equiv.` : ""}`} />
                <div className={`bubble result ${it.payload.isError ? "err" : ""}`}>{it.payload.isError ? <span>{it.payload.subtype}</span> : <span className="muted small">✅ Turn finished</span>}</div>
              </div>
            );
          case "error":
            return (
              <div key={it.seq} className="msg">
                <Who name={`Error${it.payload.category ? ` · ${it.payload.category}` : ""}`} at={it.at} />
                <div className="bubble error">
                  <pre style={{ background: "transparent", padding: 0 }}>{it.payload.message}</pre>
                </div>
              </div>
            );
          case "system":
            return (
              <div key={it.seq} className="sysline" title={fullTime(it.at)}>
                {it.text} · {clock(it.at)}
              </div>
            );
        }
      })}

    </div>
  );
}

function Composer({ session }: { session: Session }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await post<{ live: { queued: number } }>(`/api/sessions/${session.id}/messages`, { text: t, via: "web" });
      setText("");
      if (r.live.queued > 0) toast(`Queued; Claude is busy (${r.live.queued} waiting)`);
      refresh(`/api/sessions/${session.id}`);
    } catch (err) {
      toast(errorText(err), "bad");
    } finally {
      setBusy(false);
    }
  };
  const resting = session.state === "stopped" || session.state === "errored";
  return (
    <div className="composer">
      <textarea
        rows={3}
        value={text}
        placeholder={resting ? "Send a message to resume this session…" : "Send a message to this session… (Ctrl+Enter)"}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <div className="row between">
        <span className="muted small">{session.live?.busy ? "Claude is working; your message will queue." : resting ? "The session restarts from its stored conversation." : ""}</span>
        <button className="btn primary" disabled={busy || !text.trim()} onClick={() => send()}>
          Send
        </button>
      </div>
    </div>
  );
}

function SessionSidebar({ session: s, project, onDiff }: { session: Session; project?: Project; onDiff: () => void }) {
  const repos = sessionRepos(project);
  const [model, setModel] = useState("");
  const [setM, settingModel] = useAction(async () => {
    await post(`/api/sessions/${s.id}/model`, { model: model.trim() });
    setModel("");
    refresh(`/api/sessions/${s.id}`);
  }, "Model changes from the next turn");
  return (
    <div className="stack">
      <section className="card">
        <header>
          <h3>Changes</h3>
          <button className="btn sm ghost" onClick={onDiff}>
            View diff
          </button>
        </header>
        {repos.map((r) => (
          <RepoChanges key={r} sessionId={s.id} repo={r} labelled={repos.length > 1} />
        ))}
      </section>
      <section className="card">
        <h3>Session</h3>
        <dl className="kv">
          <dt>mode</dt>
          <dd className="mono">{s.permissionMode}</dd>
          <dt>cwd</dt>
          <dd className="mono">{project ? relPath(s.worktreePath, project.rootPath) || "project checkout" : s.worktreePath}</dd>
          <dt>{s.kind === "remote" ? "terminal" : "runner"}</dt>
          <dd className="mono">
            {s.tmuxTarget ?? "—"} {s.live?.connected ? <span className="pill ok">connected</span> : <span className="pill">offline</span>}
          </dd>
          <dt>claude id</dt>
          <dd className="mono" title={s.claudeSessionId ?? ""}>
            {shortId(s.claudeSessionId)}
          </dd>
          <dt>created</dt>
          <dd>{fullTime(s.createdAt)}</dd>
          {s.startedAt && (
            <>
              <dt>started</dt>
              <dd>{fullTime(s.startedAt)}</dd>
            </>
          )}
        </dl>
        <div className="row nowrap">
          <input list="session-models" placeholder={`model (${s.model ?? project?.defaultModel ?? "default"})`} value={model} onChange={(e) => setModel(e.target.value)} />
          <datalist id="session-models">
            {MODEL_SUGGESTIONS.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <button className="btn sm" disabled={!model.trim() || settingModel} onClick={() => setM()}>
            Set
          </button>
        </div>
        {s.tmuxTarget?.startsWith("tmux:") && <div className="muted small">Attach over SSH: <code>tmux attach -t switchboard</code></div>}
        {s.kind === "remote" && (
          <div className="muted small">
            Remote Control session: Claude runs in its own terminal. Its replies and tool calls are mirrored here; questions and permission prompts are answered in the Claude app or the terminal.
          </div>
        )}
      </section>
    </div>
  );
}

function RepoChanges({ sessionId, repo, labelled }: { sessionId: string; repo: string; labelled: boolean }) {
  const diff = useDiff(sessionId, repo);
  if (!diff.data) return diff.error ? <div className="muted small">{errorText(diff.error)}</div> : <Loading />;
  // In a workspace, skip repositories with nothing to show.
  if (labelled && !diff.data.files.length && !diff.data.commits.length) return null;
  return (
    <div className="stack tight">
      <div className="small">
        {labelled && <b className="mono">{repoLabel(repo)} </b>}
        <span className="plus">+{diff.data.additions}</span> <span className="minus">−{diff.data.deletions}</span>{" "}
        <span className="muted">· {diff.data.uncommitted ? `${diff.data.uncommitted} uncommitted` : "all committed"}</span>
      </div>
      <DiffFiles diff={diff.data} />
    </div>
  );
}
